/**
 * 说什么 -- the one place an Owner's words leave this shell.
 *
 * It used to be a block inside `ChatPage`, which is why talking to the Agent
 * meant standing on 对话: the composer was part of the page that drew the log.
 * Lifted out, it is mounted once beside the workspace (`OwnerConversationDock`) and
 * borrowed by the two hosts that are not the Owner shell -- the developer
 * console's 对话 page and the embedded workspace inside a Hub -- so there is
 * one implementation of "a person said something", not one per page.
 *
 * What it keeps from the old block, because each one was a fix: the draft
 * survives a failed send and a page change (`useDraft`), a prepared page
 * request is a draft until the person presses 发送 (s2's `SurfaceIntentDraft`),
 * and Enter sends while Shift+Enter makes a line (said on the send button, not under the box).
 *
 * Files ride along with the words: picked with the clip, pasted into the box,
 * or dropped on the conversation (the host hands them over through
 * `attachRef`). Each goes up the moment it is given (`/api/uploads`) and shows
 * as a chip until the message that carries it is taken; 发送 waits for the
 * ones still going up, and a chip that failed says why and holds the send
 * until it is removed.
 */

import { useEffect, useRef, useState, type MutableRefObject, type RefObject } from "react";
import { createPortal } from "react-dom";
import { api, ApiError, type ChatReceipt, type GivenFile, type MessageDelivery, type PanelEntry, type ReplyOption } from "../lib/api";
import { readDraft, useDraft } from "../lib/drafts";
import { useMessageDelivery } from "../hub/useMessageDelivery";
import { Icon } from "../components/Icon";
import { LightMotion } from "../components/LightMotion";
import { Button, Select } from "../components/rumonai";
import { fileSizeWords, GIVABLE_TYPES } from "../components/attachments/kind";
import { surfaceDraftKey, submitSurfaceIntent, type SurfaceIntentDraft } from "../panel/SurfaceIntent";

/**
 * Where the Owner was standing when they said it.
 *
 * The page, and the work surface when the page is one. It travels with the
 * words the way s2's page request does -- recorded on the submission rather
 * than hinted at in this tab -- so the Agent's plugin can tell 「主人在看审片面
 * 时说的」 from the same sentence typed on 今天. The shell adds nothing else,
 * and shows none of it.
 */
export interface MessageOrigin {
  page: string;
  panel_key?: string;
}

/**
 * Send one message, with what this submission has to say about itself.
 *
 * A plain message stays plain: a host with nothing to add calls the endpoint
 * with `text` and no more -- not with an empty object and not with an explicit
 * `undefined`, both of which are a third argument as far as anything reading
 * the call is concerned.
 */
export function sendMessage(
  sessionId: string, text: string, origin?: MessageOrigin, replyId?: string, commandId?: string, files?: GivenFile[],
) {
  const options = { ...origin, ...(replyId ? { reply_id: replyId } : {}),
    ...(commandId ? { command_id: commandId } : {}), ...(files?.length ? { files } : {}) };
  return Object.keys(options).length
    ? api.send(sessionId, text, options)
    : api.send(sessionId, text);
}

interface PendingMessage {
  command_id: string;
  text: string;
  draft: string;
  origin?: MessageOrigin;
  /** The files it carries. Kept with it, so a retry after a reload sends the same ones. */
  files?: GivenFile[];
}

interface SavedMessage {
  command_id: string;
  text: string;
  delivery: MessageDelivery;
}

interface WorkDraft { intent: SurfaceIntentDraft; text: string }
export interface ComposerWorkView { title: string; detail?: string; onReturn?: () => void }

function targetLabel(intent: SurfaceIntentDraft, panels: PanelEntry[]): string {
  if (intent.reference?.purpose === "new_task") return "新想法";
  const title = typeof intent.reference?.title === "string" && intent.reference.title.trim()
    ? intent.reference.title.trim().slice(0, 120)
    : intent.object ? intent.text.slice(0, 60) : `${panels.find(panel => panel.key === intent.panel_key)?.title || "当前界面"}的工作要求`;
  const time = intent.reference?.t;
  return typeof time === "number" && Number.isFinite(time) && time >= 0
    ? `${title} · ${String(Math.floor(time / 60)).padStart(2, "0")}:${String(Math.floor(time % 60)).padStart(2, "0")}` : title;
}

const deliveryWords: Record<MessageDelivery["state"], string> = {
  queued: "已保存，等待交给 IP。",
  waiting_capacity: "已保存，等待可用容量。尚未交给 IP。",
  owner_off: "已保存。这个 IP 已手动关闭，启用后才会处理。",
  maintenance: "已保存，维护结束后再交给 IP。",
  retrying: "已保存，正在确认投递。尚未确认 Agent 接收。",
  delivered: "Agent 已接收，正在同步对话。",
  rejected: "Agent 没有接收这条消息。",
};

/** One file in the bar: going up, up, or refused with the reason. */
interface Chip {
  key: string;
  name: string;
  size: number;
  state: "uploading" | "up" | "failed";
  /** The upload's id, once it is up. */
  id?: string;
  /** Why it was not taken, in one short clause. */
  reason?: string;
  abort?: AbortController;
}

/** The clause on a refused file's chip: the server's own when it wrote one for the Owner. */
const refusal = (err: unknown) => {
  const failure = ApiError.fromUnknown(err);
  return failure.machine ? "没有传上去" : failure.detail || failure.title;
};

/** The server refused this attempt for sure: it took nothing. */
const definite = (failure: ApiError) => failure.status >= 400 && failure.status < 500;

/** The server's own words when it wrote them for the Owner, else plain ones. */
const said = (failure: ApiError) => failure.machine ? new ApiError("没有发出去", "", null, failure.status) : failure;

export function Composer({
  suggestions,
  suggestionsTarget,
  sessionId,
  connected,
  origin,
  panels = [],
  incomingSurfaceIntent,
  onSurfaceIntentPlaced,
  onToast,
  onSent,
  onSending,
  inputRef,
  attachRef,
  compact = false,
  sendDisabled = false,
  queueAvailable = false,
  agentName,
  viewingWork,
}: {
  /** Declared starters use this same sender, draft, queue and receipt recovery. */
  suggestions?: Pick<ReplyOption, "label" | "value">[];
  /** The conversation owns their position, directly below the Agent's opening. */
  suggestionsTarget?: HTMLElement | null;
  sessionId: string;
  connected: boolean;
  origin?: MessageOrigin;
  panels?: PanelEntry[];
  incomingSurfaceIntent?: SurfaceIntentDraft | null;
  onSurfaceIntentPlaced?: (commandId: string) => void;
  onToast: (message: string, tone: "good" | "critical") => void;
  /** The words went in. Whoever is drawing the conversation should re-read it. */
  onSent?: () => void;
  onSending?: (busy: boolean) => void;
  inputRef?: RefObject<HTMLTextAreaElement>;
  /** Filled with this bar's way to take files, for a host that catches a drop. */
  attachRef?: MutableRefObject<((files: File[]) => void) | null>;
  /** The host's compact input shape; the send action stays labelled in either view. */
  compact?: boolean;
  /** Keep drafting available when the host knows a required connection is missing. */
  sendDisabled?: boolean;
  /** Host acceptance is available; this grants no panel/tool or file access. */
  queueAvailable?: boolean;
  /** Who the words go to, said in the box before anything is typed. */
  agentName?: string;
  /** Viewing is separate from the selected input target. */
  viewingWork?: ComposerWorkView;
}) {
  const [plainDraft, setPlainDraft, draftError] = useDraft("chat:" + sessionId, "");
  const [surfaceIntent, setSurfaceIntent, intentDraftError] = useDraft<SurfaceIntentDraft | null>(
    `surface-intent:${sessionId}`,
    null,
  );
  const [workDrafts, setWorkDrafts, workDraftError] = useDraft<Record<string, WorkDraft>>(`surface-drafts:${sessionId}`, {});
  const workDraftsRef = useRef(workDrafts);
  workDraftsRef.current = workDrafts;
  const selectedDraft = surfaceIntent ? surfaceDraftKey(surfaceIntent) : "";
  const draft = surfaceIntent ? workDrafts[selectedDraft]?.text ?? surfaceIntent.text : plainDraft;
  const rememberWorkDrafts = (next: Record<string, WorkDraft>) => { workDraftsRef.current = next; setWorkDrafts(next); };
  const setDraft = (text: string) => {
    if (surfaceIntent) rememberWorkDrafts({ ...workDraftsRef.current, [selectedDraft]: { intent: surfaceIntent, text } });
    else setPlainDraft(text);
  };
  // A message sent and not yet confirmed. Held in memory; storage only lets a
  // reload find it again, and sending never waits on storage.
  const [pending, setPending, pendingError] = useDraft<PendingMessage | null>(`chat-send:${sessionId}`, null);
  const [queued, setQueued, queueError] = useDraft<SavedMessage[]>(`chat-queue:${sessionId}`, []);
  const queuedRef = useRef(queued);
  queuedRef.current = queued;
  const [sending, setSending] = useState(false);
  const [startedSession, setStartedSession] = useState<string | null>(null);
  const sendingRef = useRef(false);
  const [sendError, setSendError] = useState<ApiError | null>(null);
  const fallbackRef = useRef<HTMLTextAreaElement>(null);
  const box = inputRef ?? fallbackRef;
  const draftRef = useRef(draft);
  const sessionRef = useRef(sessionId);
  draftRef.current = draft;
  sessionRef.current = sessionId;
  const placedIntent = useRef<string | null>(null);
  // The chips. The ref is the truth, read after an await; the state draws it.
  const [chips, setChips] = useState<Chip[]>([]);
  const chipsRef = useRef<Chip[]>([]);
  const uploading = useRef(new Map<string, Promise<void>>());
  const picker = useRef<HTMLInputElement>(null);
  const changeChips = (change: (chips: Chip[]) => Chip[]) => {
    chipsRef.current = change(chipsRef.current);
    setChips(chipsRef.current);
  };

  /** Each file goes up now; its chip follows it. */
  const attach = (files: File[]) => {
    // A page's work request in the bar takes no files: a drop then is let go,
    // as the clip and a paste already are.
    if (surfaceIntent || !connected) return;
    for (const file of files) {
      const key = crypto.randomUUID();
      const abort = new AbortController();
      const name = file.name || "文件";
      changeChips(all => [...all, { key, name, size: file.size, state: "uploading", abort }]);
      const job = api.upload(file, name, abort.signal).then(
        (up) => changeChips(all => all.map(chip => chip.key === key
          ? { key, name: up.name, size: up.size, state: "up", id: up.id } : chip)),
        (err) => {
          if (abort.signal.aborted) return;
          changeChips(all => all.map(chip => chip.key === key
            ? { key, name, size: file.size, state: "failed", reason: refusal(err) } : chip));
        },
      ).finally(() => uploading.current.delete(key));
      uploading.current.set(key, job);
    }
  };
  const attachNow = useRef(attach);
  attachNow.current = attach;

  /** Off the bar. One still going up is stopped, and the server keeps none of it. */
  const remove = (key: string) => {
    chipsRef.current.find(chip => chip.key === key)?.abort?.abort();
    changeChips(all => all.filter(chip => chip.key !== key));
  };

  useEffect(() => {
    if (!attachRef) return;
    attachRef.current = (files) => attachNow.current(files);
    return () => { attachRef.current = null; };
  }, [attachRef]);

  useEffect(() => () => {
    // Another conversation, or the bar is gone: what was given here stays here.
    for (const chip of chipsRef.current) chip.abort?.abort();
    chipsRef.current = [];
    setChips([]);
  }, [sessionId]);

  useEffect(() => {
    const incoming = incomingSurfaceIntent;
    if (!incoming || incoming.session_id !== sessionId) return;
    if (placedIntent.current === incoming.command_id) return;
    if (sending || pending) return;
    const key = surfaceDraftKey(incoming);
    const existing = workDraftsRef.current[key];
    const restored = existing ?? { intent: incoming, text: incoming.text };
    rememberWorkDrafts({ ...workDraftsRef.current, [key]: restored });
    setSurfaceIntent(restored.intent);
    box.current?.focus({ preventScroll: true });
    placedIntent.current = incoming.command_id;
    onSurfaceIntentPlaced?.(incoming.command_id);
  }, [incomingSurfaceIntent, sessionId, sending, pending, setSurfaceIntent, setWorkDrafts, onSurfaceIntentPlaced, box]);

  const busy = (next: boolean) => {
    sendingRef.current = next;
    setSending(next);
    onSending?.(next);
  };

  const clearSubmittedDraft = (message: PendingMessage) => {
    if (sessionRef.current !== sessionId) return;
    setStartedSession(sessionId);
    setSendError(null);
    if (draftRef.current === message.draft && message.draft.trim() === message.text) setDraft("");
    const sent = new Set((message.files ?? []).map(file => file.id));
    if (sent.size) changeChips(all => all.filter(chip => !chip.id || !sent.has(chip.id)));
  };

  /** Only the Runtime receipt moves the conversation into its ordinary record flow. */
  const delivered = (message: PendingMessage) => {
    setPending(null);
    clearSubmittedDraft(message);
    if (sessionRef.current === sessionId) onSent?.();
  };

  const saved = (message: PendingMessage, delivery: MessageDelivery) => {
    setPending(null);
    const entries = sessionRef.current === sessionId ? queuedRef.current : queued;
    const next = [...entries.filter(row => row.command_id !== message.command_id),
      { command_id: message.command_id, text: message.text, delivery }];
    if (sessionRef.current === sessionId) queuedRef.current = next;
    setQueued(next);
    clearSubmittedDraft(message);
  };

  const readReceipt = (message: PendingMessage, receipt: ChatReceipt | null | undefined) => {
    if (receipt?.obligation_id) { delivered(message); return true; }
    if (receipt?.delivery) { saved(message, receipt.delivery); return true; }
    return false;
  };

  useMessageDelivery(sessionId, queued.filter(row => row.delivery.state !== "rejected").map(row => row.command_id),
    (commandId, receipt) => {
      const current = queuedRef.current;
      if (!current.some(row => row.command_id === commandId)) return;
      if (receipt.obligation_id) {
        queuedRef.current = current.filter(row => row.command_id !== commandId);
        setQueued(queuedRef.current);
        onSent?.();
      } else if (receipt.delivery) {
        queuedRef.current = current.map(row => row.command_id === commandId
          ? { ...row, delivery: receipt.delivery! } : row);
        setQueued(queuedRef.current);
      }
    });

  /**
   * One attempt at `message`, under its own id. However it fails, whether that
   * id was taken is asked once: taken shows as sent, a definite refusal that
   * took nothing releases the words, and anything else keeps them until the
   * Owner retries (the same id, so never twice) or lets it go.
   */
  const attempt = async (message: PendingMessage) => {
    try {
      const receipt = await sendMessage(sessionId, message.text, message.origin, undefined, message.command_id, message.files);
      if (!readReceipt(message, receipt)) delivered(message);
    } catch (err) {
      const failure = ApiError.fromUnknown(err);
      const receipt = await api.chatReceipt(sessionId, message.command_id).catch(() => null);
      if (readReceipt(message, receipt)) return;
      if (receipt && definite(failure)) {
        setPending(null);
        if (sessionRef.current !== sessionId) return;
        setSendError(said(failure));
        onToast(said(failure).title, "critical");
        return;
      }
      if (sessionRef.current === sessionId) onToast("还没确认送达", "critical");
    }
  };

  const send = async (text: string) => {
    if ((!text && !chipsRef.current.length) || sendingRef.current || pending || sendDisabled
      || (!connected && (!queueAvailable || surfaceIntent || chipsRef.current.length))) return;
    // A refused file holds the send until it is taken off; files go with a
    // plain message only, never with a page's work request.
    if (!surfaceIntent && chipsRef.current.some(chip => chip.state === "failed")) return;
    const submittedDraft = draftRef.current;
    busy(true);
    setSendError(null);
    try {
      if (!surfaceIntent) {
        // 发送 waits for the files still going up, including any given meanwhile.
        while (chipsRef.current.some(chip => chip.state === "uploading"))
          await Promise.all(chipsRef.current.map(chip => uploading.current.get(chip.key)));
        // Moved to another conversation meanwhile: these words are not sent without their files.
        if (sessionRef.current !== sessionId) return;
        const ready = chipsRef.current;
        if (ready.some(chip => chip.state !== "up")) return;
        const files = ready.map(chip => ({ id: chip.id as string, name: chip.name }));
        if (!text && !files.length) return;
        const message: PendingMessage = { command_id: `chat-message-${crypto.randomUUID()}`, text,
          draft: submittedDraft, origin, ...(files.length ? { files } : {}) };
        setPending(message);
        await attempt(message);
        return;
      }
      if (surfaceIntent.session_id !== sessionId)
        throw new ApiError("没有发出去", "这项工作属于另一段对话。", null, 0);
      await submitSurfaceIntent(surfaceIntent, text);
      const remaining = { ...(sessionRef.current === sessionId ? workDraftsRef.current
        : readDraft<Record<string, WorkDraft>>(`surface-drafts:${sessionId}`, {})) };
      const key = surfaceDraftKey(surfaceIntent), newer = remaining[key];
      const nextIntent = newer && newer.text !== submittedDraft
        ? { ...surfaceIntent, command_id: `surface-intent-${crypto.randomUUID()}` } : null;
      if (nextIntent) remaining[key] = { intent: nextIntent, text: newer.text };
      else delete remaining[key];
      if (sessionRef.current === sessionId) workDraftsRef.current = remaining;
      setWorkDrafts(remaining);
      setSurfaceIntent(nextIntent);
      if (sessionRef.current === sessionId) {
        onSent?.();
      }
    } catch (err) {
      // The draft stays put: a failed send must never eat what you typed.
      const failure = said(ApiError.fromUnknown(err));
      if (sessionRef.current !== sessionId) return;
      setSendError(failure);
      onToast(failure.title, "critical");
    } finally {
      busy(false);
    }
  };

  /** Whether the kept message was taken; with `retry`, send it again under its own id. */
  const recoverMessage = async (retry: boolean) => {
    if (!pending || sendingRef.current || (!connected && !queueAvailable) || (retry && sendDisabled)) return;
    const message = pending;
    busy(true);
    setSendError(null);
    try {
      if (retry) return await attempt(message);
      // Reading is never a send: not taken yet leaves it kept.
      const receipt = await api.chatReceipt(sessionId, message.command_id).catch(() => null);
      readReceipt(message, receipt);
    } finally { busy(false); }
  };

  /**
   * Let the kept message go; the words stay in the box. Only when the receipt says nothing holds it: a message
   * the Agent took shows as sent, one the Hub still holds shows as saved, and one whose state cannot be read
   * stays kept, since sending its words again would be a second message.
   */
  const discard = async () => {
    if (!pending || sendingRef.current) return;
    const message = pending;
    busy(true);
    setSendError(null);
    try {
      const receipt = await api.chatReceipt(sessionId, message.command_id).catch(() => null);
      if (!receipt) {
        if (sessionRef.current === sessionId) onToast("还没确认送达，现在不能放弃。", "critical");
        return;
      }
      if (readReceipt(message, receipt)) return;
      setPending(null);
      box.current?.focus({ preventScroll: true });
    } finally { busy(false); }
  };
  const recoverRef = useRef(recoverMessage);
  recoverRef.current = recoverMessage;
  useEffect(() => {
    // Reopening this conversation or reconnecting only reads its old receipt.
    if (connected || queueAvailable) void recoverRef.current(false);
  }, [connected, queueAvailable, sessionId]);

  const savedTargets = Object.entries(workDrafts);
  const version = surfaceIntent?.reference?.media_version;
  const versionLabel = typeof version === "string" && version
    ? `版本 ${version.length > 16 ? version.slice(0, 8) : version}` : "";

  return (
    <>
      <form
        className={`composer pa-bounded-surface${compact ? " composer-bar" : ""}`}
        onSubmit={(e) => {
          e.preventDefault();
          void send(draft.trim());
        }}
      >
        {suggestionsTarget && suggestions?.length && startedSession !== sessionId && !draft && !surfaceIntent && !chips.length
          && !pending && !queued.length ? createPortal(<div className="chat-opening-options" role="group" aria-label="回答开场问题">
          {suggestions.map(option => <Button key={option.value} type="button" variant="secondary"
            disabled={sending || sendDisabled || (!connected && !queueAvailable)} onClick={() => {
              if (sendingRef.current) return;
              setPlainDraft(option.value);
              draftRef.current = option.value;
              void send(option.value);
            }}>{option.label}<Icon name="arrow" size={15} /></Button>)}
        </div>, suggestionsTarget) : null}
        {viewingWork || savedTargets.length || surfaceIntent ? <div className="composer-context" role="group" aria-label="本次输入">
          {savedTargets.length > 1 ? <Select label="输入目标" className="composer-target-select" value={selectedDraft} disabled={sending || Boolean(pending)}
            onValueChange={value => setSurfaceIntent(workDrafts[value]?.intent ?? null)}
            items={[{ value: "", label: `与${agentName || "Agent"}对话` },
              ...savedTargets.map(([value, saved]) => ({ value, label: targetLabel(saved.intent, panels) }))]} />
            : surfaceIntent ? <span className="composer-context-target"><span>发送至</span><strong>{targetLabel(surfaceIntent, panels)}</strong></span> : null}
          {versionLabel ? <span className="composer-context-version" title={`影片版本：${version}`}>{versionLabel}</span> : null}
          {viewingWork ? <span className="composer-context-view" role="group" aria-label="当前查看的工作"><span>当前页面</span><strong title={viewingWork.detail || viewingWork.title}>{viewingWork.title}</strong></span> : null}
          {viewingWork?.onReturn ? <button type="button" className="btn btn-quiet" onClick={viewingWork.onReturn}>返回工作</button> : null}
          {surfaceIntent ? <button type="button" className="btn btn-quiet" disabled={sending || Boolean(pending)}
            onClick={() => { if (!plainDraft.trim()) setPlainDraft(draft); setSurfaceIntent(null); box.current?.focus({ preventScroll: true }); }}>
            {plainDraft.trim() ? "返回普通对话" : "作为普通消息"}
          </button> : savedTargets.length === 1 ? <button type="button" className="btn btn-quiet" disabled={sending || Boolean(pending)}
            onClick={() => { setSurfaceIntent(savedTargets[0][1].intent); box.current?.focus({ preventScroll: true }); }}>继续草稿：{targetLabel(savedTargets[0][1].intent, panels)}</button> : null}
        </div> : null}
        {!surfaceIntent && chips.length ? (
          <ul className="composer-files" aria-label="附件">
            {chips.map(chip => (
              <li key={chip.key} className="composer-file" data-state={chip.state}>
                {chip.state === "uploading" ? <LightMotion compact state="working" /> : null}
                <span className="composer-file-name" title={chip.name}>{chip.name}</span>
                {chip.state === "failed"
                  ? <span className="composer-file-note" role="alert">{chip.reason}</span>
                  : <span className="composer-file-note">{fileSizeWords(chip.size)}</span>}
                <button type="button" className="composer-file-remove" onClick={() => remove(chip.key)}>
                  <Icon name="no" size={14} />
                  <span className="visually-hidden">移除{chip.name}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        <button
          type="button"
          className="btn btn-quiet composer-attach"
          title="添加图片、视频、音频、PDF 或文本文件；发送前可移除。"
          disabled={!connected || Boolean(surfaceIntent)}
          onClick={() => picker.current?.click()}
        >
          <Icon name="attach" size={20} />
          <span className="visually-hidden">添加文件</span>
        </button>
        <input
          ref={picker}
          type="file"
          multiple
          hidden
          accept={GIVABLE_TYPES}
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            // Cleared, so the same file can be picked again after removing it.
            e.target.value = "";
            if (files.length) attach(files);
          }}
        />
        <textarea
          ref={box}
          aria-label="说点什么"
          placeholder={connected ? surfaceIntent ? `关于${targetLabel(surfaceIntent, panels)}…` : agentName ? `给${agentName}发消息` : "发消息…" : queueAvailable ? "写下消息，保存后等待 Agent 处理" : "暂未连接，可先写下消息"}
          rows={compact ? 1 : 2}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onPaste={(e) => {
            // A picture on the clipboard is a file; words are words.
            const files = Array.from(e.clipboardData.files);
            if (!files.length || e.clipboardData.getData("text/plain") || surfaceIntent) return;
            e.preventDefault();
            attach(files);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              void send(draft.trim());
            }
          }}
        />
        <button
          type="submit"
          className="btn btn-accent composer-send"
          // The keys are said here, where a hand is about to press them, and not under the box all the time.
          title="Enter 发送，Shift+Enter 换行"
          aria-label={sending ? "发送中" : "发送"}
          aria-busy={sending}
          disabled={sending || Boolean(pending) || (!draft.trim() && !chips.length) || sendDisabled
            || (!connected && (!queueAvailable || Boolean(surfaceIntent) || chips.length > 0))
            || (!surfaceIntent && chips.some(chip => chip.state === "failed"))}
        >
          {sending ? <LightMotion compact state="working" /> : <Icon name="arrow-up" size={19} />}
        </button>
      </form>
      {pending && !sending ? <div className="composer-work-request" role="status" data-state="unconfirmed">
        <p>上一条还没确认送达。</p>
        <button type="button" className="btn" data-action="retry" disabled={(!connected && !queueAvailable) || sendDisabled} onClick={() => void recoverMessage(true)}>重试</button>
        <button type="button" className="btn btn-quiet" data-action="discard" onClick={() => void discard()}>放弃</button>
      </div> : null}
      {queued.map(entry => <div key={entry.command_id} className="composer-work-request" role="status" data-state={entry.delivery.state}>
        <div><p>{deliveryWords[entry.delivery.state]}</p><p>{entry.text}</p>
          {entry.delivery.reason ? <p>{entry.delivery.reason}</p> : null}</div>
        {entry.delivery.state === "rejected" ? <button type="button" className="btn btn-quiet" onClick={() => {
          setDraft(draftRef.current ? `${draftRef.current}\n${entry.text}` : entry.text);
          queuedRef.current = queuedRef.current.filter(row => row.command_id !== entry.command_id);
          setQueued(queuedRef.current);
          box.current?.focus({ preventScroll: true });
        }}>放回输入框</button> : null}
      </div>)}
      {[...new Set([pendingError, draftError, intentDraftError, workDraftError, queueError].filter(Boolean))].map(error =>
        <p role="alert" key={error}>{error}</p>)}

      {sendError ? (
        <p className="composer-error" role="alert">
          <strong>{sendError.title}</strong>
          {sendError.detail}
          {sendError.hint ? <span className="micro"> {sendError.hint}</span> : null}
        </p>
      ) : null}
    </>
  );
}
